import api from "@/api"
import events from "@/bl-events"
import notify from "@/utils/notify"
import globals from "@/utils/globals"
import * as gtm from "@/services/tracking/gtm"
import { adjustLinks } from "@blinkist/frontend-helpers"

const state = {
  fingerprint: null,
  segment: null,
  authenticityToken: null,
  loggedIn: null,
  hasTrialed: null,
  willRenew: null,
  plans: {
    items: [],
    yearlySavedPercents: null
  },
  abtests: {},
  supportedLanguages: globals.configSupportedLanguages,
  hasMobileApp: null,
  isIos: null,
  isAndroid: null,
  cookies: {
    submitted: null,
    consented: null,
    options: {
      necessary: true,
      preferences: false,
      statistics: false,
      marketing: false
    }
  },
  usersEmail: null,
  isInB2bOrganisation: false,
  isB2bOrganisationOwner: false,
  isB2bOrganisationMember: false,
  isInB2cMultiUserPlan: false,
  isB2cMultiUserPlanMember: false,
  isB2cMultiUserPlanOwner: false,
  isSubscriptionPartnerUser: false,
  activeB2bOrganisationOwnership: null
}

const getters = {
  abtests: (state) => state.abtests,
  authenticityToken: (state) => {
    return state.authenticityToken
  },
  isAuthenticated: (state) => {
    return !!state.authenticityToken
  },
  isAnonymous(state) {
    return state.segment === "anonymous"
  },
  hasUserAccessToContent: (state) => {
    // Users have full access to content if they are either:
    // - fully premium (segment = 'premium')
    // - or have some sort of "free access" e.g. through referral (segment = 'trial')
    // Also the loggedIn state has to be checked here as we keep the segment of a user around even after they log out.
    return state.loggedIn === true && (state.segment === "premium" || state.segment === "trial")
  },
  // (!) Note: This will be true even if the user is logged out but was formerly logged in as a premium user!
  // For most use-cases you actually might want to use `hasUserAccessToContent` or `isUserLoggedIn && isUserPremium`.
  isUserPremium: (state) => {
    return state.segment === "premium"
  },
  // (!) Note: This will be true even if the user is logged out but was formerly logged in as a basic user!
  // For most use-cases you actually might want to use `isUserLoggedIn && isUserBasic`.
  isUserBasic: (state) => {
    return state.segment === "basic"
  },
  isUserLoggedOut: (state) => {
    return state.loggedIn === false
  },
  isUserLoggedIn: (state) => {
    return state.loggedIn === true
  },
  hasTrialed: (state) => {
    return state.hasTrialed === true
  },
  willRenew: (state) => {
    return state.willRenew === true
  },
  canTrial: (state) => {
    return state.hasTrialed === false
  },
  supportedLanguages: (state) => {
    return state.supportedLanguages
  },
  displayConsentBanner: (state) => {
    return state.cookies.submitted === false
  },
  downloadAppUrl:
    (state) =>
    ({ buttonCode, platform = null, androidWebToMobileFlag = null, deeplinkPath = null }) => {
      if (!state.fingerprint) {
        return "#"
      }

      return adjustLinks.generateAdjustUrl({
        buttonCode,
        abTestVariations: state.abtests,
        fingerprint: state.fingerprint,
        platform,
        deeplinkPath: state.hasMobileApp ? deeplinkPath : null,
        androidWebToMobileFlag,
        useClassicLink: !state.hasMobileApp
      })
    },
  hasMobileApp: (state) => {
    return state.hasMobileApp
  },
  usersEmail: (state) => {
    return state.usersEmail
  },
  isInB2bOrganisation: (state) => {
    return state.isInB2bOrganisation
  },
  isB2bOrganisationOwner: (state) => {
    return state.isB2bOrganisationOwner
  },
  isB2bOrganisationMember: (state) => {
    return state.isB2bOrganisationMember
  },
  isInB2cMultiUserPlan: (state) => {
    return state.isInB2cMultiUserPlan
  },
  isB2cMultiUserPlanMember: (state) => {
    return state.isB2cMultiUserPlanMember
  },
  isB2cMultiUserPlanOwner: (state) => {
    return state.isB2cMultiUserPlanOwner
  },
  isSubscriptionPartnerUser: (state) => {
    return state.isSubscriptionPartnerUser
  },
  activeB2bOrganisationOwnership: (state) => {
    return state.activeB2bOrganisationOwnership
  }
}

const mutations = {
  fingerprint: (state, payload) => {
    state.fingerprint = payload
  },
  segment: (state, payload) => {
    state.segment = payload
  },
  hasMobileApp: (state, payload) => {
    state.hasMobileApp = payload
  },
  authenticityToken: (state, payload) => {
    state.authenticityToken = payload
  },
  loggedIn: (state, payload) => {
    state.loggedIn = payload
  },
  hasTrialed: (state, payload) => {
    state.hasTrialed = payload
  },
  willRenew: (state, payload) => {
    state.willRenew = payload
  },
  plans: (state, payload) => {
    state.plans.items = [...payload]
  },
  abtests: (state, payload) => {
    state.abtests = { ...payload }
  },
  yearlySavedPercents: (state, payload) => {
    state.plans.yearlySavedPercents = payload
  },
  supportedLanguages: (state, payload) => {
    state.supportedLanguages = payload
  },
  appStatus: (state, payload) => {
    state.hasMobileApp = payload.hasMobileApp
    state.isIos = payload.isIos
    state.isAndroid = payload.isAndroid
  },
  cookiesSubmitted: (state, payload) => {
    state.cookies = {
      ...state.cookies,
      submitted: payload
    }
  },
  cookiesConsented: (state, payload) => {
    state.cookies = {
      ...state.cookies,
      consented: payload
    }
  },
  cookiesOptions: (state, payload) => {
    state.cookies.options = {
      ...state.cookies.options,
      ...payload
    }
  },
  usersEmail: (state, payload) => {
    state.usersEmail = payload
  },
  isInB2bOrganisation: (state, payload) => {
    state.isInB2bOrganisation = payload
  },
  isB2bOrganisationOwner: (state, payload) => {
    state.isB2bOrganisationOwner = payload
  },
  isB2bOrganisationMember: (state, payload) => {
    state.isB2bOrganisationMember = payload
  },
  isInB2cMultiUserPlan: (state, payload) => {
    state.isInB2cMultiUserPlan = payload
  },
  isB2cMultiUserPlanMember: (state, payload) => {
    state.isB2cMultiUserPlanMember = payload
  },
  isB2cMultiUserPlanOwner: (state, payload) => {
    state.isB2cMultiUserPlanOwner = payload
  },
  isSubscriptionPartnerUser: (state, payload) => {
    state.isSubscriptionPartnerUser = payload
  },
  activeB2bOrganisationOwnership: (state, payload) => {
    state.activeB2bOrganisationOwnership = payload
  }
}

const actions = {
  setup: ({ commit }, payload) => {
    commit("fingerprint", payload.fingerprint)
    commit("segment", payload.segment)
    commit("authenticityToken", payload.authenticity_token)
    commit("loggedIn", !payload.guest)
    commit("supportedLanguages", payload.supported_languages)
    commit("hasTrialed", !!payload.has_trialed)
    commit("willRenew", !!payload.will_renew)
    commit("appStatus", {
      hasMobileApp: payload["has_mobile_app"],
      isIos: payload["is_ios"],
      isAndroid: payload["is_android"]
    })

    commit("usersEmail", payload.email)

    events.emitNoLegacy("setup:ready", payload)
    document.querySelector("body").classList.add("setup-ready")

    // These test categories include only those from cached pages, where mickey_mouse is called.
    // Non-cached pages trigger the categorization somewhere else, e.g. from the Rails controller.
    commit("abtests", payload.test_categories)
    events.emitNoLegacy("abtest:ready", payload.test_categories || {})
  },
  plans: ({ commit }) => {
    api
      .plans()
      .then((data) => {
        commit("plans", data.plans)
        commit("yearlySavedPercents", data.yearlySavedPercents)
        events.emit("plans:ready", data)
      })
      .catch((error) => {
        notify("Failed to fetch plans")
        events.emit("plans:error", error)
      })
  },
  organisationInfo: async ({ commit }) => {
    try {
      const data = await api.organisations.me()
      commit("isInB2bOrganisation", data.is_in_b2b_organisation)
      commit("isB2bOrganisationOwner", data.is_b2b_organisation_owner)
      commit("isB2bOrganisationMember", data.is_b2b_organisation_member)
      commit("isInB2cMultiUserPlan", data.is_in_b2c_multi_user_plan)
      commit("isB2cMultiUserPlanMember", data.is_b2c_multi_user_plan_member)
      commit("isB2cMultiUserPlanOwner", data.is_b2c_multi_user_plan_owner)
      commit("isSubscriptionPartnerUser", data.is_subscription_partner_user)
      commit("activeB2bOrganisationOwnership", data.active_b2b_organisation_ownership)
    } catch (error) {
      notify("Failed to fetch organisation info", error)
    }
  },
  loadUsersAccounts: async ({ commit }) => {
    commit("usersEmail", null)

    return api.settings
      .loadAccounts()
      .then((response) => {
        commit("usersEmail", response.accounts["primary"])
      })
      .catch(() => {
        commit("usersEmail", null)
      })
  },
  closeCookieBanner: ({ commit }) => {
    commit("cookiesSubmitted", true)
  },
  initCookies: ({ commit }, payload) => {
    commit("cookiesSubmitted", true)
    commit("cookiesConsented", payload.consented)
    commit("cookiesOptions", payload.options)

    events.emit("cookies:ready")

    if (payload.options.preferences && payload.options.statistics && payload.options.marketing) {
      gtm.loadScript()
    }
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}
